import { useEffect, useState } from "react";
import { useSignal } from "../net/signal";
import { fa } from "./cards";
import type { BreakWatch } from "./handBreak";
import { BotIcon } from "../ui/BotIcon";

/**
 * دکمه‌ی «دستِ بعد» ِ پنلِ پایانِ دست/دور، با «ادامه» ِ همه — [[BreakWatch]].
 *
 *   • بازیکنی که هنوز نزده: همان دعوتِ «بریم دستِ بعد!» با ثانیه‌شمارِ پخشِ خودکار.
 *   • بازیکنی که زده: «منتظرِ بقیه…» و چه کسی آماده است؛ پنل تا پخشِ دستِ بعد باز می‌ماند.
 *   • تماشاچی: دکمه ندارد، فقط می‌بیند چند نفر آماده‌اند.
 *
 * نامِ هر صندلی با تیک زیرش می‌آید تا معلوم باشد میز منتظرِ کیست، نه فقط «چند نفر».
 * بی مکث (سرورِ کهنه) همان دکمه‌ی پیشین است که فقط پنل را می‌بندد.
 */
export function BreakNext({ watch, label, onNext }: { watch: BreakWatch; label: string; onNext: () => void }) {
  const v = useSignal(watch.view);
  useTick(v != null);
  if (!v) return <button className="btn" onClick={onNext}>{label}</button>;

  const left = Math.max(0, Math.ceil((v.endsAt - performance.now()) / 1000));
  const done = v.ready.filter(Boolean).length;
  const count = `${fa(done)} از ${fa(v.ready.length)}`;
  const seats = (
    <div className="brk-seats" aria-label={`آماده: ${count}`}>
      {v.names.map((n, i) => (
        <span key={i} className={`brk-seat ${v.ready[i] ? "on" : ""} ${i === v.me ? "me" : ""}`}>
          <i aria-hidden>{v.ready[i] ? "✓" : "…"}</i>{v.bots[i] && i !== v.me && <BotIcon size={12} />}{i === v.me ? "تو" : n}
        </span>
      ))}
    </div>
  );

  if (v.me < 0) {
    return (
      <div className="brk">
        {/* همه زده‌اند ولی صفحه پنل را هنوز نگه داشته (PANEL_MIN_MS): «منتظر» دیگر راست نیست. */}
        <div className="muted">{done === v.ready.length ? "همه آماده‌اند — دست بعد…" : <>منتظر بازیکن‌ها · {count} آماده{left > 0 ? ` · ${fa(left)}` : ""}</>}</div>
        {seats}
      </div>
    );
  }
  if (v.pressed) {
    return (
      <div className="brk">
        <button className="btn" disabled>منتظر بقیه… {count}</button>
        {seats}
        {left > 0 && <div className="muted">اگر کسی نزند، {fa(left)} ثانیه‌ی دیگر خودش شروع می‌شود</div>}
      </div>
    );
  }
  return (
    <div className="brk">
      <button className="btn" onClick={onNext}>{left > 0 ? `${label} (${fa(left)})` : label}</button>
      {seats}
    </div>
  );
}

/** رندرِ دوباره برای ثانیه‌شمار — فقط وقتی مکثی هست. */
function useTick(on: boolean): void {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setN((n) => n + 1), 250);
    return () => clearInterval(t);
  }, [on]);
}
